import type { AnimalSplitPart } from './processor-types';

/**
 * Canonical cut & wrap / additional-charge billing rules for V2 cutsheets.
 *
 * Both documents that bill a split — the Stripe invoice (built in the web
 * `generate-invoice` page) and the billing statement PDF (built in the API's
 * `calculateBillingStatementLineItems`) — resolve prices through the helpers
 * here so the same split can't be billed two different amounts. Before this
 * module the two paths implemented the order independently and disagreed
 * about which questionnaire to read.
 */

/**
 * The candidate $/lb figures for one split, most specific first.
 *
 * All fields are "whatever the caller could find" — `undefined`/`null`/`0`
 * all mean "not set", since the underlying fields are optional and a stored
 * `0` is indistinguishable from an empty numeric input in the builder.
 */
export interface CutWrapPriceSources {
  /**
   * The $/lb stamped on the questionnaire *response* — the price the customer
   * saw and agreed to when they submitted. Most specific, so it wins.
   */
  responsePricePerPound?: number | null;
  /**
   * The custom $/lb set on the questionnaire the response was built from.
   *
   * Read this from `questionnaireResponse.questionnaire` (the form actually
   * answered), not from `split.questionnaire` (the form currently assigned) —
   * the two diverge the moment a split is reassigned after submission.
   */
  questionnairePricePerPound?: number | null;
  /**
   * The processor's own `customCutsheetPricing` rate for this animal's
   * species + inspection level.
   */
  processorCustomRate?: number | null;
  /**
   * A legacy V1 vendor cutsheet's own `pricePerPound`. V2 splits have no
   * legacy cutsheet, so this is only reached by V1 vendor sheets.
   */
  legacyCutsheetPricePerPound?: number | null;
}

/** `true` when a candidate price is a usable positive rate. */
function isSetPrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Resolves the cut & wrap $/lb of hanging weight for a split.
 *
 * Resolution order — the price stamped on the response, then the form's own
 * custom $/lb, then the processor's inspection-level custom-cutsheet rate,
 * then a legacy V1 vendor cutsheet's price. Returns `0` when nothing is set,
 * which callers surface as a $0 line rather than dropping the charge.
 * @param sources The candidate prices for this split.
 */
export function resolveCutWrapPricePerPound(
  sources: CutWrapPriceSources,
): number {
  const {
    responsePricePerPound,
    questionnairePricePerPound,
    processorCustomRate,
    legacyCutsheetPricePerPound,
  } = sources;

  if (isSetPrice(responsePricePerPound)) return responsePricePerPound;
  if (isSetPrice(questionnairePricePerPound)) return questionnairePricePerPound;
  if (isSetPrice(processorCustomRate)) return processorCustomRate;
  if (isSetPrice(legacyCutsheetPricePerPound))
    return legacyCutsheetPricePerPound;

  return 0;
}

/** Each recognised split part's share of one animal. */
const SPLIT_PART_PORTIONS: Record<AnimalSplitPart, number> = {
  whole: 1,
  half: 0.5,
  quarter: 0.25,
};

/**
 * The share of a whole animal one split represents, for prorating a
 * `per_animal` charge.
 *
 * Throws on an unrecognised or missing `splitPart` rather than inheriting
 * `getSplitMultiplier`'s permissive `return 1`: that fallback bills a
 * per-animal fee **in full** on every split it doesn't recognise, and silent
 * over-billing is the worse failure direction. This mirrors `getSplitPortion`
 * in the API's `calculateInvoiceItems`, which already throws on the same input.
 * @param splitPart The split's portion of the animal.
 */
export function perAnimalSplitPortion(
  splitPart: string | null | undefined,
): number {
  const key = (splitPart ?? '').toLowerCase() as AnimalSplitPart;
  const portion = SPLIT_PART_PORTIONS[key];

  if (portion === undefined) {
    throw new Error(
      `Unable to prorate a per-animal charge: unrecognized animal split part "${splitPart}"`,
    );
  }

  return portion;
}

/** A questionnaire's flat fee, as authored on the form. */
export interface QuestionnaireAdditionalCharge {
  label: string;
  amount: number;
  basis?: string | null;
  description?: string | null;
}

/** One additional charge, already resolved to the amount this split owes. */
export interface ResolvedAdditionalCharge {
  /** Line-item name (the charge's label). */
  label: string;
  /** Invoice line description — the label, plus the note when one is set. */
  description: string;
  /** Dollar amount for THIS split, prorated when billed per animal. */
  amount: number;
}

/**
 * Resolves a questionnaire's flat fees into the amounts one split owes.
 *
 * `per_animal` charges cover the whole head and are prorated by the split's
 * portion — a $100 fee on an animal cut into halves bills $50 to each side,
 * and a head that is only half sold therefore collects $50 and never the rest.
 * `per_split` charges bill in full on every split.
 *
 * Charges that aren't a positive finite amount are dropped. Negative amounts
 * in particular are not passed through as credit lines — nothing validates the
 * sign on the write path, and the sibling `animalHead.invoice.additionalCharges`
 * consumer already guards the same way.
 * @param charges The fees authored on the questionnaire.
 * @param splitPart The split's portion of the animal, for per-animal proration.
 */
export function resolveAdditionalCharges(
  charges: readonly QuestionnaireAdditionalCharge[] | null | undefined,
  splitPart: string | null | undefined,
): ResolvedAdditionalCharge[] {
  const applicable = (charges ?? []).filter(
    (charge) =>
      charge &&
      typeof charge.amount === 'number' &&
      Number.isFinite(charge.amount) &&
      charge.amount > 0,
  );

  if (applicable.length === 0) return [];

  // Resolved once, and only when there's something to prorate, so a job with
  // no questionnaire charges can't throw on a malformed `splitPart`.
  const portion = perAnimalSplitPortion(splitPart);

  return applicable.map((charge) => {
    const multiplier = charge.basis === 'per_split' ? 1 : portion;

    return {
      label: charge.label,
      description: charge.description
        ? `${charge.label} — ${charge.description}`
        : charge.label,
      amount: Math.round(charge.amount * multiplier * 100) / 100,
    };
  });
}
